import { ContentObjectWatcherInterface, ExtendedFormatSettings } from '@mbrtargeting/metatag-shared-types';
import { AdEngineConfigureParams, AdLibraryInterface, AdvertJson, ExternalBuildAdOptions } from '@mbrtargeting/metatag-shared-types/ad-engine';
import { isString, once } from '@mbrtargeting/metatag-utils';
import { inject, injectionTarget } from '../../../loader/decorators/inject.js';
import { ConfigResolver } from '../../../loader/essentials/config-resolver.js';
import { selectCommonSettings } from '../../../loader/essentials/config-selector.js';
import { MetaTag } from '../../../loader/essentials/init-metatag.js';
import { logger } from '../../../loader/essentials/logger.js';
import { AdslotRegistry } from '../../../loader/modules/adslot-registry.js';
import { ADSLOT_REGISTRY, CONFIG_RESOLVER, METATAG } from '../../../loader/token.js';
import { loadDependency } from '../../../utils/load-dependency.js';
import { markSdg } from '../../../utils/performance.js';
import { CONTENT_OBJECT_WATCHER, FORMAT_CONTROLLER } from '../../token.js';
import { setIdsForAdSlot } from '../../utils/adslot-data-store.js';
import { Adslot } from '../adslot-controller/adslot.js';
import { FormatController } from '../format-controller/format-controller.js';

const log = logger.logGroup({ prefix: 'System' });

declare const window: Window & {
    sdgAdLibrary?: AdLibraryInterface;
};

@injectionTarget()
export class LoadAdLibrary {

    @inject(CONFIG_RESOLVER) private configResolver!: ConfigResolver;
    @inject(FORMAT_CONTROLLER) private formatController!: FormatController;
    @inject(ADSLOT_REGISTRY) private adslotRegistry!: AdslotRegistry;
    @inject(METATAG) private metaTag!: MetaTag;
    @inject(CONTENT_OBJECT_WATCHER) private contentObjectWatcher!: ContentObjectWatcherInterface;

    constructor() {
        this.metaTag.buildAd = this.buildAd.bind(this);
    }

    #getAdLibrary = once((configParams: AdEngineConfigureParams) => new Promise<AdLibraryInterface>((resolve, reject) => {
        if (window.sdgAdLibrary?.buildAd) {
            resolve(window.sdgAdLibrary);
            return;
        }
        markSdg(`ad-library-requested_mt`);
        const { v2AdEngineFile } = this.configResolver.get(selectCommonSettings());

        loadDependency(
            v2AdEngineFile,
            () => {
                log.debug('AdEngine successfully loaded: %s', [v2AdEngineFile]);
                window.sdgAdLibrary!.configure(configParams);
                resolve(window.sdgAdLibrary!);
            },
            (event) => {
                log.critical('AdEngine could not be loaded: %s', [v2AdEngineFile]);
                reject(event);
            },
        );
    }));

    public async buildAd(json: AdvertJson, options: ExternalBuildAdOptions = {}) {
        const advertising = this.metaTag.Advertising!;
        const formatName = isString(json.format) ? json.format : (json.adFormat || '');
        const formatSettings: Partial<ExtendedFormatSettings> = this.formatController.getFormat(formatName)?.settings || {};
        const slotName = json.renderingSlot;
        const publicAdSlot = advertising.getSlotByName(slotName);
        const internalSlot = this.adslotRegistry.getAdslot(slotName) as Adslot | null;
        const { onComplete, window } = options;

        if (internalSlot?.options.makeSticky) formatSettings.slotSticky = true;

        //used in doubleverify measurement to identify format correctly
        const viewabilityId = json.viewabilityId ?? formatSettings?.formatCode;
        if (viewabilityId) {
            setIdsForAdSlot(slotName, {
                source: 'doubleverify',
                values: {
                    viewabilityId
                }
            });
        }

        const renderInstructions = async () => {
            log.debug('%s: Rendering source: MetaTag-Ad-Engine. Using JSON %o', [slotName.toUpperCase(), json]);

            //load and pass all important features / modules to ad-engine
            const library = await this.#getAdLibrary({
                adhesionUnitWatcher: advertising.getAdhesionUnitWatcher(),
                contentObjectWatcher: this.contentObjectWatcher,
                websiteFeatures: advertising.getWebsiteFeatures(),
                advertising,
            });

            //start rendering process in ad-engine
            await library.buildAd({
                onComplete,
                formatSettings,
                adSlot: publicAdSlot!,
                json,
                window,
            });
            log.debug('%s: finished rendering ad via AdLibrary.', [slotName.toUpperCase()]);
        };

        if (!publicAdSlot || !internalSlot) return log.error('AD_LIBRARY: Could not determine adSlot to render ad in. Impression will not continue. JSON was: %o', [json]);

        internalSlot.renderCreative(renderInstructions()); //don't await, will mess with stateTransitions
    }
}
